/**
 * A destructive confirmation NAMES ITS EFFECT — «احذف الحساب», never «هل أنت
 * متأكّد؟» — and states what does not change, so the reader is answering a
 * question rather than agreeing to one.
 *
 * Two structural rules, both load-bearing:
 *   • Cancel takes focus, so `Enter` on an accidental open cancels rather than
 *     destroys.
 *   • A destructive control is NEVER FILLED. `--danger` and the action red sit
 *     6° apart in hue and are indistinguishable at the same lightness, so the
 *     distinction is carried by FORM: a filled red control is always the
 *     affirmative action, and destruction is outline-only.
 */
import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '../cn';

import { Dialog } from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Names the effect, with the subject in it. */
  title: string;
  description?: ReactNode;
  /** Names the verb — «احذف المنشور», never «موافق». */
  confirmLabel: string;
  cancelLabel: string;
  intent?: 'danger' | 'warning';
  busy?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  intent = 'danger',
  busy = false,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              'min-h-11 rounded-sm border px-5 py-3 text-sm font-semibold transition-colors duration-fast',
              intent === 'danger'
                ? 'border-danger bg-transparent text-danger hover:bg-danger-soft'
                : 'border-warning bg-transparent text-warning hover:bg-warning-soft',
              'disabled:cursor-not-allowed disabled:opacity-45',
            )}
          >
            {confirmLabel}
          </button>
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-sm px-5 py-3 text-sm text-ink-muted transition-colors duration-fast hover:bg-hover-bg"
          >
            {cancelLabel}
          </button>
        </>
      }
    />
  );
}
